import { format } from "date-fns";

// Helper function to convert 24-hour time to 12-hour format with AM/PM
export const formatTime12Hour = (time24: string): string => {
  const [hours, minutes] = time24.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
  return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
};

// Helper function to safely format notification date with fallback
export const formatNotificationDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString; // Return raw string if date parsing fails
    }
    return format(date, "MMM d, yyyy h:mm a");
  } catch (error) {
    return dateString; // Return raw string if formatting fails
  }
};

// Converts a date input value ("YYYY-MM-DD") into the UTC instants that bound
// that calendar day in the browser's own timezone. The date is split into
// numbers and built with the local Date constructor on purpose:
// new Date("YYYY-MM-DD") would parse as UTC midnight instead.
export const convertLocalDateToUTCRange = (
  localDateString: string,
): { from: string | undefined; to: string | undefined } => {
  if (!localDateString) return { from: undefined, to: undefined };

  const [year, month, day] = localDateString.split("-").map(Number);

  return {
    from: new Date(year, month - 1, day, 0, 0, 0, 0).toISOString(),
    to: new Date(year, month - 1, day, 23, 59, 59, 999).toISOString(),
  };
};

// Formats a UTC timestamp for display in the browser's own timezone
export const formatUTCDateForLocalTime = (
  utcDateString: string | null,
): string => {
  if (!utcDateString) return "N/A";
  try {
    const utcDate = new Date(utcDateString);
    if (isNaN(utcDate.getTime())) {
      return "Invalid Date";
    }

    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(utcDate);
  } catch (error) {
    return "Invalid Date";
  }
};
